import React, { useEffect, useState, useRef, createElement } from 'react';
import { DownloadIcon, PrinterIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '../../components/ui/Button';
import { SalesReceipt } from '../../types';
import { IssuerHeader } from './IssuerHeader';
import { calculateSalesReceiptTotals, formatCurrency } from './utils';
export type SalesReceiptDocumentAction = 'view' | 'print' | 'download';
type SalesReceiptModalProps = {
  receipt: SalesReceipt;
  onClose: () => void;
  initialAction?: SalesReceiptDocumentAction;
};
export function SalesReceiptModal({
  receipt,
  onClose,
  initialAction = 'view'
}: SalesReceiptModalProps) {
  const printableRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const totals = calculateSalesReceiptTotals(receipt.items);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const printReceipt = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    window.setTimeout(() => document.body.classList.remove('printing-test-modal'), 500);
  };
  const downloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    try {
      await captureToA4Pdf(printableRef.current, `BMED_${receipt.salesReceiptNumber.replace(/[^a-z0-9-_]/gi, '_')}.pdf`);
    } catch (error) {
      console.error('PDF generation failed', error);
      window.alert('Failed to generate the PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  useEffect(() => {
    if (initialAction === 'print') {
      const timer = window.setTimeout(printReceipt, 0);
      return () => window.clearTimeout(timer);
    }
    if (initialAction === 'download') {
      const timer = window.setTimeout(() => void downloadPdf(), 0);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, []);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 print:bg-white print:p-0" role="dialog" aria-modal="true" aria-labelledby="sales-receipt-modal-title" data-id="element-1925" data-mp="2ub84kdnknxpy">
      <div className="test-modal-print max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white shadow-2xl print:max-h-none print:max-w-none print:overflow-visible print:rounded-none print:shadow-none" data-id="element-1926" data-mp="26fc9d5tq16rv">
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3 print:hidden" data-id="element-1927" data-mp="2gd0f0qsv7yot">
          <h2 id="sales-receipt-modal-title" className="text-lg font-semibold text-slate-900" data-id="element-1928" data-mp="2kgg9phldjoyv">
            Sales Receipt {receipt.salesReceiptNumber}
          </h2>
          <div className="flex items-center gap-2" data-id="element-1929" data-mp="3vsh3xc10c5m0">
            <Button variant="outline" size="sm" onClick={printReceipt} data-id="element-1930" data-mp="1k76pz9zllkzk">
              <PrinterIcon className="mr-1 h-4 w-4" data-id="element-1931" data-mp="1g5boo5ho37ls" /> Print
            </Button>
            <Button size="sm" onClick={downloadPdf} disabled={isGeneratingPdf} data-id="element-1932" data-mp="1g7g33hd6zp0b">
              <DownloadIcon className="mr-1 h-4 w-4" data-id="element-1933" data-mp="1bd21uybumnnq" />
              {isGeneratingPdf ? 'Generating…' : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close sales receipt" data-id="element-1934" data-mp="11g780sqitbfs">
              <XIcon className="h-5 w-5" data-id="element-1935" data-mp="sg0vpa6k6hi7" />
            </button>
          </div>
        </div>

        <article ref={printableRef} className="billing-document bg-white p-0 text-slate-950" data-id="element-1936" data-mp="niinjm0d86b9">
          <header className="flex flex-col gap-6 border-b-2 border-slate-900 pb-5 sm:flex-row sm:items-start sm:justify-between" data-id="element-1937" data-mp="2mrfoy0ys2z2p">
            <IssuerHeader data-id="element-1938" data-mp="1ej9q1lp1s0yb" />
            <div className="text-left sm:text-right" data-id="element-1939" data-mp="1el2y95fb6t8f">
              <h1 className="text-2xl font-bold tracking-[0.14em] text-slate-800 sm:text-3xl" data-id="element-1940" data-mp="1jimcw6fx580w">
                SALES RECEIPT
              </h1>
              <p className="mt-3 font-semibold" data-id="element-1941" data-mp="15ibp5c9wo9jy">
                Sales Receipt# {receipt.salesReceiptNumber}
              </p>
            </div>
          </header>

          <section className="my-7 grid gap-5 border-b border-slate-200 pb-7 sm:grid-cols-2" data-id="element-1942" data-mp="vss3xzyq03lx">
            <div data-id="element-1943" data-mp="10jqojbqqrnuz">
              <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1944" data-mp="10ka7mvs4oial">
                Bill to
              </h2>
              <p className="text-lg font-bold text-slate-900" data-id="element-1945" data-mp="mugew2di5dvl">
                {receipt.billTo}
              </p>
              {receipt.customerPhone && <p className="mt-1 text-slate-600" data-id="element-1946" data-mp="cu622ivdq8gy">{receipt.customerPhone}</p>}
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 sm:justify-self-end" data-id="element-1947" data-mp="hshbishrigyn">
              <dt className="font-semibold text-slate-600" data-id="element-1948" data-mp="3ucbosbcwq3t7">Number</dt>
              <dd className="font-mono font-semibold text-slate-900" data-id="element-1949" data-mp="3pea2zio3jqe1">
                {receipt.salesReceiptNumber}
              </dd>
              <dt className="font-semibold text-slate-600" data-id="element-1950" data-mp="86y5iqozvv2u">Visit Date</dt>
              <dd data-id="element-1951" data-mp="36aieg3vzydw">{formatDate(receipt.visitDate)}</dd>
              <dt className="font-semibold text-slate-600" data-id="element-1952" data-mp="35imt5aai0nm">Sales Person</dt>
              <dd data-id="element-1953" data-mp="h5a69ljks990">{receipt.salespersonName}</dd>
            </dl>
          </section>

          <div className="overflow-x-auto" data-id="element-1954" data-mp="c89zf8ewj2gu">
            <table className="min-w-[680px] w-full border-collapse" data-id="element-1955" data-mp="c5fhbnjd1tql">
              <thead data-id="element-1956" data-mp="qv37bsm3d5z0">
                <tr className="border-b-2 border-slate-300 bg-slate-100" data-id="element-1957" data-mp="qvmrzh34m8gq">
                  <th scope="col" className="w-16 p-2.5 text-left text-xs font-bold uppercase text-slate-600" data-id="element-1958" data-mp="lymgurrf6fir">
                    No.
                  </th>
                  <th scope="col" className="p-2.5 text-left text-xs font-bold uppercase text-slate-600" data-id="element-1959" data-mp="vxc30wqcmm4t">
                    Item &amp; Description
                  </th>
                  <th scope="col" className="w-20 p-2.5 text-right text-xs font-bold uppercase text-slate-600" data-id="element-1960" data-mp="1cwveqi955e7e">
                    Qty
                  </th>
                  <th scope="col" className="w-32 p-2.5 text-right text-xs font-bold uppercase text-slate-600" data-id="element-1961" data-mp="1lpoushwewhmb">
                    Rate
                  </th>
                  <th scope="col" className="w-36 p-2.5 text-right text-xs font-bold uppercase text-slate-600" data-id="element-1962" data-mp="1voeemg7vcc57">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1963" data-mp="68f1yp51k4kt">
                {receipt.items.map((item, index) => <tr key={item.id} className="border-b border-slate-200" data-id="element-1964" data-mp="6aj3j0fgwrhx" data-mpi={index}>
                    <td className="p-2.5 text-slate-600" data-id="element-1965" data-mp="2jk9dhgf99ug2">{index + 1}</td>
                    <td className="p-2.5 font-medium text-slate-900" data-id="element-1966" data-mp="2erpx6bp6ol7k">
                      {item.itemDescription}
                    </td>
                    <td className="p-2.5 text-right" data-id="element-1967" data-mp="2aqmnk6xqbles">{item.quantity}</td>
                    <td className="p-2.5 text-right" data-id="element-1968" data-mp="25sl1dd50b5z3">
                      {formatCurrency(item.rate)}
                    </td>
                    <td className="p-2.5 text-right font-semibold" data-id="element-1969" data-mp="32fg98k6whqvr">
                      {formatCurrency(item.quantity * item.rate)}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <dl className="ml-auto mt-7 w-full max-w-xs" data-id="element-1970" data-mp="2oikq1ywxqj1z">
            <div className="flex justify-between py-1" data-id="element-1971" data-mp="2ok5fja4e7bsz">
              <dt className="text-slate-600" data-id="element-1972" data-mp="1i2ib92knka34">Subtotal:</dt>
              <dd className="font-medium" data-id="element-1973" data-mp="1d2vy0t4tqtub">{formatCurrency(totals.subtotal)}</dd>
            </div>
            <div className="flex justify-between border-b border-slate-300 py-1" data-id="element-1974" data-mp="3d55b4pn23chh">
              <dt className="text-slate-600" data-id="element-1975" data-mp="3d789py29ydxt">TOT Tax (5%):</dt>
              <dd className="font-medium" data-id="element-1976" data-mp="3i5jkcb0e8sh3">{formatCurrency(totals.totalTax)}</dd>
            </div>
            <div className="flex justify-between py-3 text-lg font-bold" data-id="element-1977" data-mp="337zgx8j5b5d1">
              <dt data-id="element-1978" data-mp="384zfkydo15u4">Total:</dt>
              <dd className="text-blue-700" data-id="element-1979" data-mp="386asbnp39nxd">
                {formatCurrency(totals.totalAmount)}
              </dd>
            </div>
          </dl>

          <footer className="mt-14 flex items-end justify-between border-t border-slate-200 pt-5 text-xs text-slate-500" data-id="element-1980" data-mp="4osl9ih54ky0">
            <p data-id="element-1981" data-mp="3m6br8xlsmla7">Independent non-invoice cash sale.</p>
            <div className="w-44 border-t border-slate-800 pt-1 text-center font-semibold uppercase text-slate-700" data-id="element-1982" data-mp="3m3pmt6u78lw7">
              Authorized Signature
            </div>
          </footer>
        </article>
      </div>
    </div>;
}
function formatDate(value: string) {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const pageWidth = 210;
  const pageHeight = 297;
  const marginTop = 17.78;
  const marginRight = 10.16;
  const marginBottom = 17.78;
  const marginLeft = 12.7;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const pixelsPerMillimetre = sourceCanvas.width / contentWidth;
  const sliceHeight = Math.floor(contentHeight * pixelsPerMillimetre);
  let cursor = 0;
  let page = 0;
  while (cursor < sourceCanvas.height) {
    const height = Math.min(sliceHeight, sourceCanvas.height - cursor);
    const slice = document.createElement('canvas');
    slice.width = sourceCanvas.width;
    slice.height = height;
    const context = slice.getContext('2d');
    if (!context) break;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, slice.width, slice.height);
    context.drawImage(sourceCanvas, 0, cursor, sourceCanvas.width, height, 0, 0, slice.width, height);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', marginLeft, marginTop, contentWidth, height / pixelsPerMillimetre);
    cursor += height;
    page += 1;
  }
  pdf.save(filename);
}